<template>
    <ion-page>
        <ion-header :translucent="true">
            <ion-toolbar>
                <ion-buttons slot="start">
                    <ion-menu-button color="primary"></ion-menu-button>
                </ion-buttons>
                <ion-title>Create Sale</ion-title>
            </ion-toolbar>
        </ion-header>

        <ion-content :fullscreen="true">
            <ion-header collapse="condense">
                <ion-toolbar>
                    <ion-title size="large">Create Sale</ion-title>
                </ion-toolbar>
            </ion-header>

            <div id="container">
                <div v-if="leadCode" class="lead-banner">
                    Converting lead <b>{{ leadCode }}</b>. The form is filled from the lead &mdash; check it,
                    add the warranty days and tap Save. The lead waits for this Sale's approval.
                </div>
                <div v-else class="lead-banner muted-banner">
                    Walk-in customer or a sale without a lead? Choose <b>Walk-in</b> as the source of sale.
                    If the client already has an open lead, convert that lead instead.
                </div>

                <ion-item>
                    <ion-label position="floating">Client Phone <span class="required">*</span></ion-label>
                    <ion-input mode="ios" :value="postData['client_phone']" type="tel"
                        @input="postData['client_phone']=$event.target.value" @ionBlur="lookup"></ion-input>
                    <ion-button slot="end" size="small" @click="lookup">Find</ion-button>
                </ion-item>
                <div v-if="found" class="found" :class="found.type">
                    <template v-if="found.type === 'client'"><b>Existing client:</b> {{ found.summary.name }}<span v-if="found.summary.company"> · {{ found.summary.company }}</span> · {{ found.summary.ships }} ship(s).</template>
                    <template v-else-if="found.type === 'lead'"><b>Known prospect:</b> details copied from lead {{ found.summary.from_lead }}.</template>
                    <template v-else><b>New client:</b> no client or earlier lead has this number.</template>
                    <div v-if="found.open_leads.length && !leadId">
                        Open leads: {{ found.open_leads.map(l => l.lead_code + ' (' + l.product + ', ' + l.owner + ')').join('; ') }}.
                        If this sale is one of them, convert that lead instead.
                    </div>
                </div>
                <ion-item>
                    <ion-label position="floating">Company Name<span class="required">*</span></ion-label>
                    <ion-input mode="ios" v-model="postData['company_name']" @ionBlur="emptySuggestions"
                        @keydown="companyNameChanged($event)"></ion-input>
                </ion-item>

                <ion-list v-if="companySuggestions.length>0">
                    <ion-list-header lines="full">
                        <ion-label color="secondary">Suggestions</ion-label>
                    </ion-list-header>
                    <ion-item v-for="(com,i) of companySuggestions" :key="i">
                        <ion-label @click="setCompanyDetails(com)" color="secondary">{{com.company_name}} <span
                                v-if="com.tracking_ship_last_date!=null">*</span></ion-label>
                    </ion-item>
                </ion-list>
                <ion-item>
                    <ion-label position="floating">Company Address <span class="required">*</span></ion-label>
                    <ion-input mode="ios" :value="postData['company_address']"
                        @input="postData['company_address']=$event.target.value"></ion-input>
                </ion-item>
                <ion-item>
                    <ion-label position="floating">Client Name <span class="required">*</span></ion-label>
                    <ion-input mode="ios" :value="postData['client_name']"
                        @input="postData['client_name']=$event.target.value">
                    </ion-input>
                </ion-item>
                <ion-list v-if="clients.length>0 && postData['client_name']==''">
                    <ion-list-header lines="full">
                        <ion-label color="secondary">Suggestions</ion-label>
                    </ion-list-header>
                    <ion-item v-for="(clnt,i) of clients" :key="i">
                        <ion-label @click="setClientDetails(clnt)" color="secondary">{{clnt.client_name}} <span
                                v-if="clnt.account_manager=='Yes'">*</span></ion-label>
                    </ion-item>
                </ion-list>

                <div class="deal-box">
                    <DealLines v-if="products.length" v-model="deal" :products="products" :month-options="monthOptions" />
                    <div v-else class="muted">Loading products...</div>
                </div>

                <ion-item>
                    <ion-label position="floating">Warranty / free service days</ion-label>
                    <ion-input mode="ios" v-model="postData['warantry_days']" type="number" placeholder="product default"></ion-input>
                </ion-item>
                <ion-item>
                    <ion-label position="floating">Source of Sale <span class="required">*</span></ion-label>
                    <ion-select mode="ios" interface="action-sheet" v-model="postData['source_of_sale']">
                        <ion-select-option v-for="(sourceOfSale,i) of sourcesOfSale" :key="i" :value="sourceOfSale">
                            {{sourceOfSale}}</ion-select-option>
                    </ion-select>
                </ion-item>
                <ion-item>
                    <ion-label position="floating">Sales By</ion-label>
                    <ion-input mode="ios" :disabled="true" :value="username"></ion-input>
                </ion-item>
                <ion-item>
                    <ion-label position="floating">Remarks</ion-label>
                    <ion-input mode="ios" :value="postData['remarks']" @input="postData['remarks']=$event.target.value">
                    </ion-input>
                </ion-item>
                <ion-button expand="block" :disabled="saving" @click="save()" color="primary">{{ saving ? 'Saving...' : 'Save' }}</ion-button>
            </div>
        </ion-content>
    </ion-page>
</template>

<script>
    import {
        IonButtons,
        IonButton,
        IonContent,
        IonHeader,
        IonMenuButton,
        IonPage,
        IonTitle,
        IonToolbar,
        IonList,
        IonListHeader,
        IonItem,
        IonLabel,
        IonSelect,
        IonSelectOption,
        alertController,
        IonInput,
    } from '@ionic/vue';
    import { closeCircle } from 'ionicons/icons';
    import DealLines from './sales-leads/DealLines.vue';
    import { loadMeta, leadApi } from '../salesLeads';
    import { emptyDeal, dealFromSaved, dealPayload } from '../deals';

    const EMPTY = {
        company_name: '',
        company_address: '',
        client_name: '',
        client_phone: '',
        warantry_days: '',
        source_of_sale: '',
        remarks: '',
    };

    export default {
        name: 'Create Sale',
        components: {
            IonButtons,
            IonButton,
            IonContent,
            IonHeader,
            IonMenuButton,
            IonPage,
            IonTitle,
            IonToolbar,
            IonItem,
            IonLabel,
            IonSelect,
            IonSelectOption,
            IonInput,
            IonList,
            IonListHeader,
            DealLines,
        },
        data() {
            return {
                username: "",
                meta: null,
                sourcesOfSale: ['Traditional Marketing', 'Telemarketing', 'Organic Sale', 'Walk-in'],
                postData: { ...EMPTY },
                deal: emptyDeal(),
                found: null,
                lookedUp: '',
                saving: false,
                companies: [],
                companySuggestions: [],
                clients: [],
                // Set when opened from a lead's "Convert to Sale" (/create-sale?lead_id=12).
                leadId: null,
                leadCode: null,
            }
        },
        computed: {
            products() {
                return this.meta && this.meta.products ? this.meta.products : [];
            },
            monthOptions() {
                return this.meta && this.meta.contract_month_options ? this.meta.contract_month_options : undefined;
            },
        },
        watch: {
            '$route.query.lead_id'(id) {
                if (this.$route.path === '/create-sale') {
                    this.loadLead(id);
                }
            }
        },
        methods: {
            // Prefill from the lead (its product deal included). The server re-checks that the lead can still be converted.
            loadLead(id) {
                this.leadId = null;
                this.leadCode = null;
                if (!id) {
                    return;
                }
                this.$http.post('salesLead/get', {
                    username: window.localStorage.username,
                    password: window.localStorage.password,
                    lead_id: id,
                }).then(response => {
                    const prefill = response.data.sale_prefill;
                    if (!prefill) {
                        this.presentResponseAlert('Alert', 'This lead cannot be converted now. Move it to Proposal Sent or Negotiation first; only the owner can convert.');
                        return;
                    }
                    for (const key of Object.keys(EMPTY)) {
                        if (prefill[key] !== undefined && prefill[key] !== null) {
                            this.postData[key] = prefill[key];
                        }
                    }
                    if (prefill.jahaji_product_id) {
                        this.deal = dealFromSaved(prefill, this.products);
                    }
                    // The Sale form requires an 11-digit local number (e.g. 01711000111).
                    let phone = String(prefill.client_phone || '').replace(/\D/g, '');
                    if (phone.startsWith('880')) {
                        phone = phone.substring(2);
                    }
                    this.postData.client_phone = phone;
                    this.lookedUp = phone;
                    this.leadId = prefill.sales_lead_id;
                    this.leadCode = response.data.lead.lead_code;
                }).catch(() => {});
            },
            // Phone first: an existing client (or an earlier lead) fills the empty fields.
            lookup() {
                const phone = String(this.postData.client_phone || '');
                if (phone.replace(/\D/g, '').length < 6 || phone === this.lookedUp) {
                    return;
                }
                this.lookedUp = phone;
                leadApi('lookupPhone', { phone }, true).then(response => {
                    this.found = response.data;
                    const fill = response.data.fill || {};
                    const map = { contact_name: 'client_name', company_name: 'company_name', address: 'company_address' };
                    for (const from of Object.keys(map)) {
                        if (fill[from] && !this.postData[map[from]]) {
                            this.postData[map[from]] = fill[from];
                        }
                    }
                }).catch(() => {});
            },
            emptySuggestions() {
                setTimeout(() => {
                    this.companySuggestions = []
                }, 500);
            },
            async presentResponseAlert(type, message) {
                const alert = await alertController.create({ header: type + '!', message: message, buttons: ['OK'] });
                await alert.present();
            },
            async loadData() {
                this.$http.get("getCompanies").then(response => {
                    this.companies = response.data;
                });
            },
            companyNameChanged(event) {
                setTimeout(() => {
                    this.companySuggestions = [];
                    if (this.postData['company_name'].length > 2) {
                        const comps = this.companies.filter(com => {
                            return com.company_name.toLowerCase().includes(event.target.value
                                .toLowerCase());
                        });
                        for (const i in comps) {
                            this.companySuggestions.push(comps[i])
                            if (i > 3) {
                                break;
                            }
                        }
                    }
                }, 500);
            },
            setCompanyDetails(com) {
                this.postData['company_name'] = com.company_name;
                this.postData['company_address'] = com.company_address;
                this.companySuggestions = [];
                this.$http.get('getClient', {
                    params: {
                        id: com.id
                    }
                }).then(response => {
                    this.clients = response.data;
                });
            },
            setClientDetails(client) {
                this.postData['client_name'] = client.client_name;
                this.postData['client_phone'] = client.mobile;
            },
            reset() {
                this.leadId = null;
                this.leadCode = null;
                this.found = null;
                this.lookedUp = '';
                this.postData = { ...EMPTY };
                this.deal = emptyDeal(this.products);
            },
            async save() {
                const requireds = {
                    client_phone: 'Client phone', company_name: 'Company name', company_address: 'Company address',
                    client_name: 'Client name', source_of_sale: 'Source of sale',
                };
                for (const field of Object.keys(requireds)) {
                    if (String(this.postData[field] || '').trim() === '') {
                        this.presentAlert(requireds[field]);
                        return;
                    }
                }
                if (String(this.postData.client_phone).length != 11) {
                    this.presentResponseAlert('Alert', 'Client Phone must be 11 digit');
                    return;
                }
                if (!this.deal.jahaji_product_id) {
                    this.presentAlert('Product');
                    return;
                }
                const data = {
                    ...this.postData,
                    ...dealPayload(this.deal),
                    username: window.localStorage.username,
                    password: window.localStorage.password,
                    sales_lead_id: this.leadId || '',
                };
                this.saving = true;
                this.$http.post('createSale', data).then(response => {
                    this.saving = false;
                    if (response.status == 200) {
                        const convertedLeadId = this.leadId;
                        this.reset();
                        this.presentResponseAlert('Success', 'Sale created Successfully');
                        this.loadData();
                        if (convertedLeadId) {
                            this.$router.replace('/sales-leads/' + convertedLeadId);
                        }
                    }
                }).catch(() => {
                    this.saving = false;
                });
            },
            async presentAlert(message) {
                const alert = await alertController.create({ header: 'Alert!', message: message + " is required", buttons: ['OK'] });
                await alert.present();
            },
        },
        async mounted() {
            this.username = window.localStorage.name;
            this.loadData();
            this.meta = await loadMeta().catch(() => null);
            if (this.meta && this.meta.sources) {
                this.sourcesOfSale = this.meta.sources;
            }
            this.deal = emptyDeal(this.products);
            this.loadLead(this.$route.query.lead_id);
        },
        setup() {
            return {
                closeCircle
            }
        },

    }
</script>

<style scoped>
    #container {
        text-align: center;
        position: absolute;
        left: 0;
        right: 0;
    }

    #container strong {
        font-size: 20px;
        line-height: 26px;
    }

    #container p {
        font-size: 16px;
        line-height: 22px;
        color: #8c8c8c;
        margin: 0;
    }

    #container a {
        text-decoration: none;
    }

    .required {
        color: rgb(233, 0, 0)
    }

    .lead-banner {
        text-align: left;
        margin: 12px 16px 4px;
        padding: 10px 12px;
        border-radius: 10px;
        background: #eff6ff;
        border: 1px solid #bfdbfe;
        color: #1e3a8a;
        font-size: 0.86rem;
        line-height: 1.4;
    }

    .muted-banner {
        background: #f8fafc;
        border-color: #e2e8f0;
        color: #475569;
    }

    .found {
        text-align: left;
        margin: 8px 16px;
        padding: 8px 12px;
        border-radius: 10px;
        font-size: 0.82rem;
        line-height: 1.4;
    }

    .found.client { background: #ecfdf5; border: 1px solid #a7f3d0; }
    .found.lead { background: #eff6ff; border: 1px solid #bfdbfe; }
    .found.none { background: #f8fafc; border: 1px solid #e2e8f0; }

    .deal-box {
        margin: 12px 16px;
        padding: 12px;
        border: 1px solid #e2e8f0;
        border-radius: 14px;
        background: #fff;
    }

    .muted {
        color: #64748b;
        font-size: 0.85rem;
    }
</style>
